/**
 * Primitivos da DSL do engine Silverlight (SE.TradezoneChart.Finance).
 *
 * Porte 1:1 de FormulaBase.cs / FormulaData.cs do WebChartSilverlight — a MESMA
 * estrutura de loop e as MESMAS regras de NaN/seed do original, para que os
 * calculators migrados sejam bit-comparáveis ao oráculo .NET
 * (tools/silverlight-oracle/OracleRunner).
 *
 * Semântica auditada: .claude/skills/migrate-indicator-fromSilverL8/DSL.md
 *
 * Convenções:
 * - Entrada/saída: number[] com index 0 = barra mais ANTIGA (mesma orientação
 *   dos calculators do odin). NaN = warm-up/dado ausente (nunca undefined aqui;
 *   a conversão NaN→undefined é responsabilidade do calculator, na borda com o
 *   chart).
 * - Escalares fazem broadcast elementwise (equivalente a FormulaType.Const +
 *   MakeSameLength/FillTo do engine para séries de mesmo comprimento).
 * - NaN segue IEEE nos aritméticos (propaga) e Math.max/min (propaga) —
 *   idêntico ao C#.
 */

const at = (s, i) => (typeof s === "number" ? s : s[i]);
const lenOf = (a, b) =>
  typeof a === "number"
    ? b.length
    : typeof b === "number"
      ? a.length
      : a.length;

/** Operação binária elementwise com broadcast de escalar. */
function op2(a, b, fn) {
  const n = lenOf(a, b);
  const out = new Array(n);
  for (let i = 0; i < n; i++) out[i] = fn(at(a, i), at(b, i));
  return out;
}

export const add = (a, b) => op2(a, b, (x, y) => x + y);
export const sub = (a, b) => op2(a, b, (x, y) => x - y);
export const mul = (a, b) => op2(a, b, (x, y) => x * y);
export const div = (a, b) => op2(a, b, (x, y) => x / y);
/** MAX(f1,f2) do engine — Math.Max propaga NaN (igual ao C#). */
export const max2 = (a, b) => op2(a, b, Math.max);
/** MIN(f1,f2) do engine. */
export const min2 = (a, b) => op2(a, b, Math.min);
/** ABS(f) do engine. */
export const abs = (f) => f.map(Math.abs);

/**
 * Relacionais do engine — retornam série 1.0/0.0. Comparação com NaN é false
 * ⇒ NaN vira 0 (NÃO propaga), conforme DSL.md. `IF(a<=b,…)` etc. usam isto.
 */
export const le = (a, b) => op2(a, b, (x, y) => (x <= y ? 1 : 0));
export const ge = (a, b) => op2(a, b, (x, y) => (x >= y ? 1 : 0));
export const lt = (a, b) => op2(a, b, (x, y) => (x < y ? 1 : 0));
export const gt = (a, b) => op2(a, b, (x, y) => (x > y ? 1 : 0));
export const eq = (a, b) => op2(a, b, (x, y) => (x === y ? 1 : 0));

/**
 * IF(cond, a, b) do engine (FormulaBase.IF): `cond[i]==0 ? b : a`. Como os
 * relacionais nunca produzem NaN (viram 0), o ramo é determinístico. Escalares
 * fazem broadcast. Base do DMZ/DMF do DDI.
 */
export function iff(cond, a, b) {
  const n = cond.length;
  const out = new Array(n);
  for (let i = 0; i < n; i++) out[i] = cond[i] === 0 ? at(b, i) : at(a, i);
  return out;
}

/**
 * SUM(f, N) — soma rolling do engine (FormulaBase.SUM). Porte EXATO do loop
 * (soma corrida subtraindo f[i-N-1]; janela efetiva N+1 termos; emite a partir
 * de i>N; NaN na entrada → NaN e não entra na soma). N=0 = soma cumulativa.
 */
export function sum(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len).fill(NaN);
  let d = 0;
  for (let i = 0; i < len; i++) {
    if (!Number.isNaN(f[i])) {
      d += f[i];
      if (NI !== 0) {
        if (i > NI && !Number.isNaN(f[i - NI - 1])) {
          d -= f[i - NI - 1];
          nf[i] = d;
        } else nf[i] = NaN;
      } else nf[i] = d;
    } else nf[i] = NaN;
  }
  return nf;
}

/**
 * REF(f, N) — desloca a série N barras para trás (REF(C,1) = fechamento
 * anterior). Porte exato de FormulaBase.REF: N>0 deixa NaN nas N primeiras
 * posições; N<0 referencia o futuro e deixa NaN nas |N| últimas.
 */
export function ref(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len).fill(0);
  for (let i = len + Math.min(NI - 1, -1); i >= Math.max(0, NI); i--) {
    nf[i] = f[i - NI];
  }
  if (NI < 0) {
    for (let i = len - 1; i >= len + NI; i--) nf[i] = NaN;
  } else if (NI <= len) {
    for (let i = NI - 1; i >= 0; i--) nf[i] = NaN;
  }
  return nf;
}

/**
 * MA(f, N) — média móvel aritmética. Porte exato de FormulaBase.MA, incluindo
 * a soma corrida com as regras de NaN do original e N=0 = média cumulativa.
 */
export function ma(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len).fill(0);
  let d = 0;
  for (let i = 0; i < len; i++) {
    if (!Number.isNaN(f[i])) d += f[i];
    else nf[i] = NaN;

    if (NI !== 0 && i >= NI - 1) {
      if (!Number.isNaN(f[i - NI + 1])) {
        if (Number.isNaN(f[i])) nf[i] = NaN;
        else nf[i] = d / NI;
        d -= f[i - NI + 1];
      } else nf[i] = NaN;
    } else {
      if (NI === 0) nf[i] = d / (i + 1);
      else nf[i] = NaN;
    }
  }
  return nf;
}

/**
 * DMA(f, A, Start) — recorrência exponencial out[i] = f[i]*A + out[i-1]*(1-A).
 * Porte exato de FormulaBase.DMA:
 * - Start=N>0: out[0..N-2]=NaN e out[N-1] = média simples dos N primeiros
 *   (NaN na janela ⇒ seed NaN);
 * - Start=0: out[0] = f[0];
 * - f[i] NaN ⇒ out[i] NaN; out[i-1] NaN ⇒ out[i] = f[i] (REINICIA).
 */
export function dma(f, a, start = 0) {
  const len = f.length;
  const nf = new Array(len).fill(0);
  let sum = 0;
  for (let i = 0; i < start && i < len; i++) {
    sum += f[i];
    nf[i] = NaN;
  }
  if (start > 0 && start <= len) nf[start - 1] = sum / start;

  for (let i = start; i < len; i++) {
    if (i > 0) {
      if (!Number.isNaN(f[i])) {
        if (!Number.isNaN(nf[i - 1])) nf[i] = f[i] * a + nf[i - 1] * (1 - a);
        else nf[i] = f[i];
      } else nf[i] = NaN;
    } else nf[i] = f[i];
  }
  return nf;
}

/**
 * SMA(f, N, M) — a "SMA chinesa" do legado: DMA(f, M/N, N). Com M=1 equivale
 * ao smoothing de Wilder com seed de média simples (base dos RSI-style:
 * VRSI, AreaRSI, …). NÃO é média aritmética — essa é ma().
 */
export const sma = (f, n, m) => dma(f, m / n, Math.trunc(n));

/** EMA(f, N) = DMA(f, 2/(N+1)), seed out[0]=f[0]. */
export const ema = (f, n) => dma(f, 2 / (n + 1));

/** WilderMA(f, N) = DMA(f, 1/N), seed out[0]=f[0]. */
export const wilderMa = (f, n) => dma(f, 1 / n);

/** SQR(f) — quadrado elementwise (NaN propaga). */
export const sqr = (f) => f.map((x) => x * x);

/**
 * SQRT(f) — raiz elementwise. Math.sqrt de negativo = NaN (igual ao C#).
 * Importante em variância: E[X²]−E[X]² pode dar negativo minúsculo por
 * cancelamento de float → NaN, e o engine reproduz exatamente isso.
 */
export const sqrt = (f) => f.map(Math.sqrt);

/**
 * LN(f) — logaritmo natural elementwise (FormulaBase.LN = Math.Log). Log de
 * negativo = NaN e de zero = −Infinity, igual ao C#.
 */
export const ln = (f) => f.map(Math.log);

/**
 * VARP(f, N) — variância populacional via E[X²] − E[X]², EXATAMENTE como o
 * engine: `MA(SQR(f),N) - SQR(MA(f,N))`. Mesma ordem de operações → mesmo
 * resultado de float (incl. negativos minúsculos).
 */
export const varp = (f, n) => sub(ma(sqr(f), n), sqr(ma(f, n)));

/**
 * VAR(f, N) — variância. Porte de FormulaBase.VAR:
 * N=0 → f; N=1 → VARP(f,1); N≥2 → VARP(f,N) · N/(N-1) (correção de Bessel = AMOSTRAL).
 */
export function variance(f, n) {
  if (n === 0) return f.slice();
  if (n === 1) return varp(f, 1);
  return mul(varp(f, n), n / (n - 1));
}

/** STD(f, N) = SQRT(VAR(f, N)) — desvio-padrão amostral (Bessel) do engine. */
export const std = (f, n) => sqrt(variance(f, n));

/**
 * HHV(f, N) — maior valor numa janela de N. Porte de FormulaBase.HHV:
 * warm-up `N-1` NaN; `f[i]` NaN ⇒ out[i] NaN; N=0 = máximo cumulativo.
 *
 * Implementação O(n) por deque monotônica (#368 — caps de período subiram a
 * 1000; o O(n·N) ingênuo virava hitch de FPS a cada tick). Semântica IDÊNTICA
 * ao loop original bit a bit — provada em __tests__/hhvLlv.equivalence.test.js
 * contra a implementação antiga e pelos testes de paridade com o oráculo .NET:
 * - qualquer NaN DENTRO da janela envenena a saída (Math.max(…,NaN)=NaN no
 *   original) — rastreado por contador de NaN na janela;
 * - N=0 (cumulativo) IGNORA NaN (só pula a barra) — assimetria preservada;
 * - N<0: janela vazia ⇒ -Infinity (f[i] NaN ⇒ NaN), como no original;
 * - N>len ⇒ tudo NaN (o loop de saída nunca roda).
 */
export function hhv(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len).fill(0);
  if (NI === 0) {
    let M = -Infinity;
    for (let i = 0; i < len; i++) {
      if (Number.isNaN(f[i])) nf[i] = NaN;
      else {
        M = Math.max(M, f[i]);
        nf[i] = M;
      }
    }
    return nf;
  }
  if (NI < 0) {
    for (let i = 0; i < len; i++) nf[i] = Number.isNaN(f[i]) ? NaN : -Infinity;
    return nf;
  }
  for (let i = 0; i < Math.min(NI - 1, len); i++) nf[i] = NaN;
  if (NI - 1 >= len) return nf;
  // Deque de índices com valores decrescentes; NaN nunca entra na deque
  // (contado à parte, pois envenena a janela inteira).
  const deq = new Int32Array(len);
  let head = 0;
  let tail = 0;
  let nanCount = 0;
  for (let i = 0; i < len; i++) {
    const v = f[i];
    if (Number.isNaN(v)) nanCount++;
    else {
      while (tail > head && f[deq[tail - 1]] <= v) tail--;
      deq[tail++] = i;
    }
    const lo = i - NI + 1; // início da janela [lo, i]
    if (lo > 0 && Number.isNaN(f[lo - 1])) nanCount--;
    while (tail > head && deq[head] < lo) head++;
    if (i >= NI - 1) nf[i] = nanCount > 0 ? NaN : f[deq[head]];
  }
  return nf;
}

/** LLV(f, N) — menor valor numa janela de N (espelho de HHV; mesma deque O(n)). */
export function llv(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len).fill(0);
  if (NI === 0) {
    let M = Infinity;
    for (let i = 0; i < len; i++) {
      if (Number.isNaN(f[i])) nf[i] = NaN;
      else {
        M = Math.min(M, f[i]);
        nf[i] = M;
      }
    }
    return nf;
  }
  if (NI < 0) {
    for (let i = 0; i < len; i++) nf[i] = Number.isNaN(f[i]) ? NaN : Infinity;
    return nf;
  }
  for (let i = 0; i < Math.min(NI - 1, len); i++) nf[i] = NaN;
  if (NI - 1 >= len) return nf;
  const deq = new Int32Array(len);
  let head = 0;
  let tail = 0;
  let nanCount = 0;
  for (let i = 0; i < len; i++) {
    const v = f[i];
    if (Number.isNaN(v)) nanCount++;
    else {
      while (tail > head && f[deq[tail - 1]] >= v) tail--;
      deq[tail++] = i;
    }
    const lo = i - NI + 1;
    if (lo > 0 && Number.isNaN(f[lo - 1])) nanCount--;
    while (tail > head && deq[head] < lo) head++;
    if (i >= NI - 1) nf[i] = nanCount > 0 ? NaN : f[deq[head]];
  }
  return nf;
}

/**
 * RSI SMA-chinesa do Silverlight (no PREÇO): usado por StochRSI/AreaRSI.
 * `SMA(MAX(C-C₋₁,0),N,1) / SMA(|C-C₋₁|,N,1) · 100`. NÃO é o rsi (Wilder d3fc)
 * do odin — é a variante do engine legado.
 */
/**
 * HHVBARS(f, N) — nº de barras desde a MAIOR máxima da janela (FormulaBase.
 * HHVBARS). Janela `[i-N, i]` (N+1 termos); empate fica com a barra MAIS
 * RECENTE (`>=`); NaN nunca vence a comparação; N=0 = desde o topo histórico.
 * Sem warm-up NaN — emite desde a barra 0.
 */
export function hhvBars(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len);
  let lastMax = 0;
  let m = -Infinity;
  for (let i = 0; i < len; i++) {
    if (NI === 0) {
      if (f[i] >= m) {
        lastMax = i;
        m = f[i];
      }
    } else {
      m = -Infinity;
      for (let j = Math.max(0, i - NI); j <= i; j++) {
        if (f[j] >= m) {
          lastMax = j;
          m = f[j];
        }
      }
    }
    nf[i] = i - lastMax;
  }
  return nf;
}

/** LLVBARS(f, N) — nº de barras desde a MENOR mínima da janela. Espelho de HHVBARS. */
export function llvBars(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const nf = new Array(len);
  let lastMin = 0;
  let m = Infinity;
  for (let i = 0; i < len; i++) {
    if (NI === 0) {
      if (f[i] <= m) {
        lastMin = i;
        m = f[i];
      }
    } else {
      m = Infinity;
      for (let j = Math.max(0, i - NI); j <= i; j++) {
        if (f[j] <= m) {
          lastMin = j;
          m = f[j];
        }
      }
    }
    nf[i] = i - lastMin;
  }
  return nf;
}

export function rsiSma(close, n) {
  const diff = sub(close, ref(close, 1));
  return mul(div(sma(max2(diff, 0), n, 1), sma(abs(diff), n, 1)), 100);
}

/**
 * MAXVALUE(f) / MINVALUE(f) — redução ESCALAR sobre a série inteira, PULANDO NaN
 * (porte de FormulaBase.MAXVALUE/MINVALUE: `M < f[i]` / `M > f[i]` — comparação
 * com NaN é false, então NaN nunca atualiza M). Retornam número (não série).
 */
export function maxValue(f) {
  let M = -Infinity;
  for (let i = 0; i < f.length; i++) if (M < f[i]) M = f[i];
  return M;
}
export function minValue(f) {
  let M = Infinity;
  for (let i = 0; i < f.length; i++) if (M > f[i]) M = f[i];
  return M;
}

/**
 * CalcLinearRegression — regressão least-squares da janela `[start-count+1 .. start]`
 * de `f`, com x = 0..count-1. Retorna {a, b} (intercepto em x=0, inclinação).
 * Porte exato de FormulaBase.CalcLinearRegression (mesma ordem de somas). NaN se
 * a janela não couber (start < count-1).
 */
export function calcLinearRegression(f, start, count) {
  if (start < count - 1) return { a: NaN, b: NaN };
  const AVGX = (count - 1) / 2;
  let SUMXY = 0;
  let SUMX2 = 0;
  let AVGY = 0;
  for (let j = 0; j < count; j++) {
    let k = start - count + 1 + j;
    if (k >= f.length) k = f.length - 1;
    SUMXY += j * f[k];
    SUMX2 += j * j;
    AVGY += f[k];
  }
  AVGY /= count;
  const b = (SUMXY - count * AVGX * AVGY) / (SUMX2 - count * AVGX * AVGX);
  const a = AVGY - b * AVGX;
  return { a, b };
}

/**
 * LR(f, N) — reta de regressão linear ESTÁTICA sobre as ÚLTIMAS N barras (porte
 * de FormulaBase.LR com Start=0): NaN em tudo exceto `[len-N .. len-1]`, onde
 * `out[i] = a + b·(i-(len-N))`. É o canal "congelado" da janela final — NÃO é a
 * regressão rolling do tsma/linearForecast.
 */
export function lr(f, n) {
  const NI = Math.trunc(n);
  const len = f.length;
  const out = new Array(len).fill(NaN);
  if (len - NI > 0) {
    const { a, b } = calcLinearRegression(f, len - 1, NI);
    const j = len - NI;
    for (let i = j; i < len; i++) out[i] = a + b * (i - j);
  }
  return out;
}
